import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Megaphone, Star, Zap } from "lucide-react";
import { CompanyMark } from "@/components/jobs/CompanyMark";
import { brandFor } from "@/lib/careers/brands";
import { FEATURED_EMPLOYERS } from "@/lib/careers/links";

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const STACK = "relative flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3 px-3 py-3";
const TRAIL = "absolute right-2 bottom-2 sm:static shrink-0";
const tileClass = (active) => `${STACK} border text-left bg-white transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0F141B] ${active ? "border-[#0F141B] bg-[#F7F8F9]" : "hairline hover:border-[#8A94A2]"}`;

const Tile = ({ employer, source, active, onSelect, index }) => {
  const brand = brandFor(employer.name);
  const id = slug(employer.name);
  const count = typeof source?.count === "number" ? `${source.count}${source.capped ? "+" : ""} open role${source.count === 1 ? "" : "s"}` : null;
  const body = (
    <>
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px]" style={{ background: brand.color }} />
      <span aria-hidden="true" className="absolute right-2 top-1.5 font-mono-label text-[9px] text-[#B8BEC7]">{String(index + 1).padStart(2, "0")}</span>
      <CompanyMark company={employer.name} size="md" testId={`featured-logo-${id}`} />
      <span className="min-w-0 flex-1">
        <span data-testid={`featured-name-${id}`} className="block font-display text-[12px] sm:text-sm font-bold text-[#0F141B] leading-tight break-words">{employer.name}</span>
        <span data-testid={`featured-meta-${id}`} className="block mt-0.5 text-[10px] leading-snug text-[#5F6168]">{employer.external ? "Meta Careers site" : count || employer.blurb}</span>
      </span>
      {employer.external ? <ArrowUpRight size={14} aria-hidden="true" className={`${TRAIL} text-[#71747B]`} /> : active && <Check size={14} aria-hidden="true" className={TRAIL} style={{ color: brand.color }} />}
    </>
  );
  if (employer.external) {
    return <a data-testid={`featured-employer-${id}`} href={employer.external} target="_blank" rel="noopener noreferrer" aria-label={`${employer.name} data center jobs, opens Meta Careers in a new tab`} className={tileClass(false)}>{body}</a>;
  }
  return <button type="button" data-testid={`featured-employer-${id}`} aria-pressed={active} onClick={() => onSelect(active ? "all" : employer.name)} className={tileClass(active)}>{body}</button>;
};

const RolesTile = ({ total }) => (
  <div data-testid="featured-roles-tile" className={`${STACK} border border-[#0FBF77] bg-[#F0FBF6] text-left`}>
    <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-[#0FBF77]" />
    <span className="w-10 h-10 shrink-0 grid place-items-center bg-white border border-[#0FBF77]/40"><Zap size={16} aria-hidden="true" className="text-[#0A8F5A]" /></span>
    <span className="min-w-0">
      <span data-testid="featured-roles-count" className="block font-display text-xl font-extrabold leading-none text-[#0A8F5A]">{typeof total === "number" ? `${Math.floor(total / 100) * 100 || total}+` : "—"}</span>
      <span className="block mt-1 text-[10px] leading-snug text-[#3D4049]">open roles · live feeds</span>
    </span>
  </div>
);

const InviteTile = () => (
  <Link to="/careers#employers" data-testid="featured-invite-tile" className={`${STACK} border border-dashed border-[#0F141B] bg-white text-left transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#F7F8F9]`}>
    <span className="w-10 h-10 shrink-0 grid place-items-center bg-[#0F141B] text-white"><Megaphone size={16} aria-hidden="true" /></span>
    <span className="min-w-0 flex-1">
      <span className="block font-display text-[12px] sm:text-sm font-bold text-[#0F141B] leading-tight">Your company here</span>
      <span className="block mt-0.5 text-[10px] leading-snug text-[#5F6168]">Promote your roles here</span>
    </span>
    <ArrowUpRight size={14} aria-hidden="true" className={`${TRAIL} text-[#0F141B]`} />
  </Link>
);

export const FeaturedEmployers = ({ sources, company, onSelect, total }) => {
  const byName = Object.fromEntries((sources || []).map((source) => [source.name, source]));
  return (
    <div data-testid="featured-employers" className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p data-testid="featured-employers-label" className="font-mono-label text-[11px] !tracking-normal text-[#3D4049] inline-flex items-center gap-2"><Star size={13} aria-hidden="true" className="text-[#0FBF77]" />Where the industry is hiring</p>
        <p className="text-[11px] text-[#71747B]"><Link to="/careers#employers" data-testid="featured-partner-link" className="underline underline-offset-4 text-[#0A8F5A] hover:text-[#0F141B] mr-2">Hiring? Promote your roles on DCYB</Link>· Tap a company to see only its roles{company !== "all" ? " · " : ""}{company !== "all" && <button type="button" data-testid="featured-clear-company" onClick={() => onSelect("all")} className="underline underline-offset-4 text-[#0F141B]">Show all employers</button>}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2.5">
        {FEATURED_EMPLOYERS.map((employer, index) => <Tile key={employer.name} employer={employer} index={index} source={byName[employer.name]} active={company === employer.name} onSelect={onSelect} />)}
        <RolesTile total={total} />
        <InviteTile />
      </div>
    </div>
  );
};
